import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import type { SignUpFormData, SignUpState } from '../hooks/useSignup';
import {
  validateText,
  validateMobile,
  isValidEmail,
  validatePasswords,
  CREATE_PASSWORD_MESSAGE,
  PASSWORD_TOOLTIP_SPECIAL_CHARACTERS,
} from '@/utils/formValidators';
import { VerifyAccountCard } from './VerifyAccountCard';
import { PrivacyConsent } from './PrivacyConsent';
import { CountryCodeSelect } from './CountryCodeSelect';
import { AccountVerifiedDialog } from './AccountVerifiedDialog';
import { SupportWarningDialog } from './SupportWarningDialog';
import { DEFAULT_PHONE_CODE } from '@/config/countryCodes';
import styles from '../AuthPage.module.css';

/**
 * CreateAccountCard — email sign up, shown next to the sign-in card.
 *
 * Mirrors Flutter WebSignUpPage + WebSignupBloc (email sign up). Once the account is
 * created, the card switches to the verify-email state (Flutter VerifyAccCard).
 *
 * Form fields and validators (from Flutter web_signup_page.dart / FormValidators):
 *   - First Name, Last Name (required — validateText)
 *   - Mobile No with country code (validateMobile with the per-country digit counts; duplicate check)
 *   - Email ID (isValidEmail; duplicate check)
 *   - Create Password, Re-type Password (validatePasswords)
 *   - Privacy/T&C consent checkbox (required)
 */

export const CREATE_ACCOUNT_CARD_ID = 'create-account';
export const CREATE_ACCOUNT_FIRST_FIELD_ID = 'sign-up-first-name';

interface CreateAccountCardProps {
  /** Owned by AuthPage, which hides the sign-in card while the verify-email state shows */
  signup: SignUpState;
}

export function CreateAccountCard({ signup }: CreateAccountCardProps) {
  const { t } = useLocalization();

  const {
    phase,
    isLoading,
    error,
    isEmailDuplicate,
    isMobileDuplicate,
    registeredEmail,
    disableResendEmail,
    warningWithSupport,
    warningMessage,
    accountVerifiedDialog,
    handleSignUp,
    handleResendVerification,
    handleReSignUp,
    handleLogout,
    handleAccountVerifiedDialogClose,
    clearError,
    clearDuplicate,
    onResendTimerEnd,
  } = signup;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState(DEFAULT_PHONE_CODE);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToPolicy, setAgreeToPolicy] = useState(false);

  // Validation errors
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  // Password-policy warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  useEffect(() => {
    if (warningWithSupport && warningMessage) setShowWarningDialog(true);
  }, [warningWithSupport, warningMessage]);

  // ── Validation (mirrors Flutter FormValidators) ────────────────

  const validate = useCallback(() => {
    // Flutter's validateText accepts a blank name, after which the bloc trims it and silently
    // stops; here a blank name gets the "is required" message instead.
    const fnErr = validateText(firstName.trim(), t('First Name'), t);
    const lnErr = validateText(lastName.trim(), t('Last Name'), t);

    // The duplicate messages are Flutter's validator results while the duplicate flag is set
    const phErr = isMobileDuplicate
      ? t('Mobile number already exists. Please use a different mobile number.')
      : validateMobile(phone, countryCode, t);

    const emErr = isEmailDuplicate
      ? t('Email already exists. Please use a different email address.')
      : isValidEmail(email.trim(), t);

    const pwErr = validatePasswords({ pass: password }, t);
    const cpErr = validatePasswords({ pass: password, confirmPass: confirmPassword, isConfirm: true }, t);

    setFirstNameError(fnErr);
    setLastNameError(lnErr);
    setPhoneError(phErr);
    setEmailError(emErr);
    setPasswordError(pwErr);
    setConfirmPasswordError(cpErr);

    return !(fnErr || lnErr || phErr || emErr || pwErr || cpErr);
  }, [
    firstName, lastName, phone, countryCode, email, password, confirmPassword,
    isEmailDuplicate, isMobileDuplicate, t,
  ]);

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    clearError();
    if (!validate() || !agreeToPolicy) return;

    const formData: SignUpFormData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      countryCode,
      email: email.trim().toLowerCase(),
      password,
      confirmPassword,
      referralCode: '', // referral code validation is commented out in Flutter email signup
      agreeToPolicy,
    };

    handleSignUp(formData);
  }, [
    validate, agreeToPolicy, firstName, lastName, phone, countryCode, email,
    password, confirmPassword, handleSignUp, clearError,
  ]);

  // ── Verify-email state ────────────────────────────────────────

  if (phase === 'verifyEmail') {
    return (
      <>
        <VerifyAccountCard
          email={registeredEmail}
          disableResendBtn={disableResendEmail}
          onResend={handleResendVerification}
          onReSignUp={handleReSignUp}
          onLogout={handleLogout}
          onTimerEnd={onResendTimerEnd}
          isLoading={isLoading}
        />
        {accountVerifiedDialog && (
          <AccountVerifiedDialog
            showContinueApply={accountVerifiedDialog.showContinueApply}
            onClose={handleAccountVerifiedDialogClose}
          />
        )}
      </>
    );
  }

  // ── Sign up form ──────────────────────────────────────────────

  return (
    <section id={CREATE_ACCOUNT_CARD_ID} className={styles.card} aria-labelledby="create-account-title">
      <h2 id="create-account-title" className={styles.title}>{t('Create your account')}</h2>

      {error && !warningWithSupport && (
        <div className={styles.errorBanner} role="alert">{t(error)}</div>
      )}

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={CREATE_ACCOUNT_FIRST_FIELD_ID}>
              {t('First Name')} <span className={styles.required}>*</span>
            </label>
            <input
              id={CREATE_ACCOUNT_FIRST_FIELD_ID}
              type="text"
              className={`${styles.input} ${firstNameError ? styles.inputError : ''}`}
              placeholder={t('Enter your first name')}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value.replace(/[^a-zA-Z\s]/g, ''));
                if (firstNameError) setFirstNameError('');
              }}
              autoComplete="given-name"
              disabled={isLoading}
            />
            {firstNameError && <span className={styles.fieldError}>{firstNameError}</span>}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="sign-up-last-name">
              {t('Last Name')} <span className={styles.required}>*</span>
            </label>
            <input
              id="sign-up-last-name"
              type="text"
              className={`${styles.input} ${lastNameError ? styles.inputError : ''}`}
              placeholder={t('Enter your last name')}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value.replace(/[^a-zA-Z\s]/g, ''));
                if (lastNameError) setLastNameError('');
              }}
              autoComplete="family-name"
              disabled={isLoading}
            />
            {lastNameError && <span className={styles.fieldError}>{lastNameError}</span>}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-up-email">
            {t('Email ID')} <span className={styles.required}>*</span>
          </label>
          <input
            id="sign-up-email"
            type="email"
            className={`${styles.input} ${(emailError || isEmailDuplicate) ? styles.inputError : ''}`}
            placeholder={t('Enter your Email Id')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
              if (isEmailDuplicate) clearDuplicate('email');
            }}
            autoComplete="email"
            disabled={isLoading}
          />
          {(emailError || isEmailDuplicate) && (
            <span className={styles.fieldError}>
              {/* Flutter errorText while the duplicate flag is set: ErrorConstants.emailExist */}
              {emailError || t('Email already exists!')}
            </span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-up-mobile">
            {t('Mobile No')} <span className={styles.required}>*</span>
          </label>
          <div className={styles.phoneRow}>
            <CountryCodeSelect
              value={countryCode}
              onChange={(code) => {
                setCountryCode(code);
                if (phoneError) setPhoneError('');
              }}
              disabled={isLoading}
            />
            <input
              id="sign-up-mobile"
              type="tel"
              className={`${styles.input} ${(phoneError || isMobileDuplicate) ? styles.inputError : ''}`}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/[^0-9\s-]/g, ''));
                if (phoneError) setPhoneError('');
                if (isMobileDuplicate) clearDuplicate('mobile');
              }}
              autoComplete="tel"
              inputMode="numeric"
              disabled={isLoading}
            />
          </div>
          {(phoneError || isMobileDuplicate) && (
            <span className={styles.fieldError}>
              {/* Flutter errorText while the duplicate flag is set: ErrorConstants.phoneNoExist */}
              {phoneError || t('Phone no already exists!')}
            </span>
          )}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-up-password">
            {t('Create Password')} <span className={styles.required}>*</span>
            <span
              className={styles.hint}
              title={t(CREATE_PASSWORD_MESSAGE) + PASSWORD_TOOLTIP_SPECIAL_CHARACTERS}
            >
              ℹ
            </span>
          </label>
          <div className={styles.passwordWrapper}>
            <input
              id="sign-up-password"
              type={showPassword ? 'text' : 'password'}
              className={`${styles.input} ${passwordError ? styles.inputError : ''}`}
              placeholder={t('Enter your Password')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError('');
              }}
              autoComplete="new-password"
              disabled={isLoading}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {passwordError && <span className={styles.fieldError}>{passwordError}</span>}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="sign-up-confirm-password">
            {t('Re-type your password')} <span className={styles.required}>*</span>
          </label>
          <div className={styles.passwordWrapper}>
            <input
              id="sign-up-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              className={`${styles.input} ${confirmPasswordError ? styles.inputError : ''}`}
              placeholder={t('Re-type your password')}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmPasswordError) setConfirmPasswordError('');
              }}
              autoComplete="new-password"
              disabled={isLoading}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showConfirmPassword ? '👁' : '👁‍🗨'}
            </button>
          </div>
          {confirmPasswordError && <span className={styles.fieldError}>{confirmPasswordError}</span>}
        </div>

        <PrivacyConsent checked={agreeToPolicy} onChange={setAgreeToPolicy} />

        <button type="submit" className={styles.primaryBtn} disabled={!agreeToPolicy || isLoading}>
          {t('Continue')}
        </button>
      </form>

      {showWarningDialog && (
        <SupportWarningDialog
          message={warningMessage}
          onOk={() => {
            setShowWarningDialog(false);
            clearError();
          }}
        />
      )}
    </section>
  );
}
